// All API endpoint paths. Import from here — never hardcode paths in components or services.

export const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://localhost:8001';

const V1 = '/api/admin/v1';

// ── Auth endpoints (matches backend router.py prefix /api/admin/v1/auth) ──
export const AUTH_ENDPOINTS = {
  LOGIN: `${V1}/auth/login`,
  LOGOUT: `${V1}/auth/logout`,
  ME: `${V1}/auth/me`,
  REFRESH: `${V1}/auth/token/refresh`,
  MFA_SETUP: `${V1}/auth/mfa/setup`,
  MFA_SETUP_CONFIRM: `${V1}/auth/mfa/setup/confirm`,
  MFA_VERIFY: `${V1}/auth/mfa/verify`,
  MFA_DISABLE: `${V1}/auth/mfa/disable`,
  // Returns the permission codes granted to the current admin (e.g. ["content.publish"]).
  MY_PERMISSIONS: `${V1}/auth/me/permissions`,
  // Password reset flow (unauthenticated)
  FORGOT_PASSWORD: `${V1}/auth/forgot-password`,
  RESET_PASSWORD: `${V1}/auth/reset-password`,
  // Invite acceptance flow (unauthenticated)
  ACCEPT_INVITE: `${V1}/auth/accept-invite`,
} as const;

// ── Genre endpoints ──
export const GENRE_ENDPOINTS = {
  LIST:               `${V1}/genres`,
  CREATE:             `${V1}/genres`,
  UPDATE:             (id: string) => `${V1}/genres/${id}`,
  DEACTIVATE:         (id: string) => `${V1}/genres/${id}`,
  REACTIVATE:         (id: string) => `${V1}/genres/${id}/reactivate`,
  DELETE_TRANSLATION: (id: string, lang: string) => `${V1}/genres/${id}/translations/${lang}`,
} as const;

// ── Series endpoints ──
export const SERIES_ENDPOINTS = {
  LIST:      `${V1}/series`,
  CREATE:    `${V1}/series`,
  DETAIL:    (id: string) => `${V1}/series/${id}`,
  UPDATE:    (id: string) => `${V1}/series/${id}`,
  DELETE:    (id: string) => `${V1}/series/${id}`,
  PUBLISH:     (id: string) => `${V1}/series/${id}/publish`,
  UNPUBLISH:   (id: string) => `${V1}/series/${id}/unpublish`,
  SCHEDULE:    (id: string) => `${V1}/series/${id}/schedule`,
  UNSCHEDULE:  (id: string) => `${V1}/series/${id}/unschedule`,
  PAYWALL_SIMULATION: (id: string) => `${V1}/series/${id}/paywall-simulation`,
  PAYWALL_ACTIVATE:   (id: string) => `${V1}/series/${id}/paywall-activate`,
  PAYWALL_PAUSE:      (id: string) => `${V1}/series/${id}/paywall-pause`,
} as const;

// ── Episodes endpoints ──
// Episodes are a flat resource — series_id is a query param on LIST, body field on CREATE.
export const EPISODES_ENDPOINTS = {
  LIST:               `${V1}/episodes`,
  CREATE:             `${V1}/episodes`,
  DETAIL:             (id: string) => `${V1}/episodes/${id}`,
  UPDATE:             (id: string) => `${V1}/episodes/${id}`,
  DELETE:             (id: string) => `${V1}/episodes/${id}`,
  PUBLISH:            (id: string) => `${V1}/episodes/${id}/publish`,
  UNPUBLISH:          (id: string) => `${V1}/episodes/${id}/unpublish`,
  SCHEDULE:           (id: string) => `${V1}/episodes/${id}/schedule`,
  UNSCHEDULE:         (id: string) => `${V1}/episodes/${id}/unschedule`,
  DELETE_TRANSLATION: (id: string, lang: string) => `${V1}/episodes/${id}/translations/${lang}`,
} as const;

// ── Admin user management endpoints ──
export const ADMIN_USERS_ENDPOINTS = {
  ME: `${V1}/users/me`,
  CHANGE_PASSWORD: `${V1}/users/me/password`,
  LIST: `${V1}/users`,
  CREATE: `${V1}/users`,
  DETAIL: (id: string) => `${V1}/users/${id}`,
  UPDATE: (id: string) => `${V1}/users/${id}`,
  SUSPEND: (id: string) => `${V1}/users/${id}/suspend`,
  REACTIVATE: (id: string) => `${V1}/users/${id}/reactivate`,
  RESET_PASSWORD: (id: string) => `${V1}/users/${id}/reset-password`,
  RESEND_INVITE: (id: string) => `${V1}/users/${id}/resend-invite`,
} as const;

// ── Permissions / RBAC endpoints ──
export const PERMISSIONS_ENDPOINTS = {
  LIST: `${V1}/permissions`,
  ROLE_PERMISSIONS: (role: string) => `${V1}/permissions/roles/${role}`,
  UPDATE_ROLE_PERMISSION: (role: string, permissionId: string) =>
    `${V1}/permissions/roles/${role}/${permissionId}`,
  RESET_DEFAULTS: `${V1}/permissions/reset-defaults`,
} as const;

// ── Audit log endpoints ──
export const AUDIT_LOGS_ENDPOINTS = {
  LIST: `${V1}/audit/logs`,
  EXPORT: `${V1}/audit/logs/export`,
} as const;

// ── Finance endpoints ──
export const TRANSACTIONS_ENDPOINTS = {
  LIST: `${V1}/finance/transactions`,
  DETAIL: (id: string) => `${V1}/finance/transactions/${id}`,
} as const;

// ── Users endpoints ──
export const USERS_ENDPOINTS = {
  SEARCH: `${V1}/users/search`,
  DETAIL: (id: string) => `${V1}/users/${id}`,
  SUSPEND: (id: string) => `${V1}/users/${id}/suspend`,
  LOOKUP_BY_PHONE: `${V1}/users/lookup-by-phone`,
} as const;

// ── Analytics endpoints ──
export const ANALYTICS_ENDPOINTS = {
  DASHBOARD: `${V1}/analytics/dashboard`,
  REPORTS: `${V1}/analytics/reports`,
} as const;

// ── Asset endpoints ──
export const ASSET_ENDPOINTS = {
  UPLOAD_INTENT: `${V1}/assets/upload-intent`,
  LIST:          `${V1}/assets`,
  DETAIL:        (id: string) => `${V1}/assets/${id}`,
  CONFIRM:       (id: string) => `${V1}/assets/${id}/confirm-upload`,
  CANCEL:        (id: string) => `${V1}/assets/${id}/cancel`,
  PREVIEW_URL:   (id: string) => `${V1}/assets/${id}/preview-url`,
} as const;

// ── Video endpoints ──
export const VIDEO_ENDPOINTS = {
  UPLOAD_INTENT:  `${V1}/videos/upload-intent`,
  LIST:           `${V1}/videos`,
  DETAIL:         (id: string) => `${V1}/videos/${id}`,
  UPDATE:         (id: string) => `${V1}/videos/${id}`,
  DELETE:         (id: string) => `${V1}/videos/${id}`,
  RESET_INTENT:   (id: string) => `${V1}/videos/${id}/upload-intent`,
  SYNC:           (id: string) => `${V1}/videos/${id}/sync`,
  QC:             (id: string) => `${V1}/videos/${id}/qc`,
  QC_OVERRIDE:    (id: string) => `${V1}/videos/${id}/qc-override`,
  RETRY:          (id: string) => `${V1}/videos/${id}/retry`,
  PREVIEW_URL:    (id: string) => `${V1}/videos/${id}/preview-url`,
} as const;

// ── Artist endpoints ──
export const ARTIST_ENDPOINTS = {
  LIST:         `${V1}/artists`,
  CREATE:       `${V1}/artists`,
  DETAIL:       (id: string) => `${V1}/artists/${id}`,
  UPDATE:       (id: string) => `${V1}/artists/${id}`,
  STATUS:       (id: string) => `${V1}/artists/${id}/status`,
  DELETE:           (id: string) => `${V1}/artists/${id}`,
  FLAG_DUPLICATE:   (id: string) => `${V1}/artists/${id}/flag-duplicate`,
  MERGE:            `${V1}/artists/merge`,
  DELETE_TRANSLATION: (id: string, lang: string) => `${V1}/artists/${id}/translations/${lang}`,
  SERIES_CAST:  {
    LIST:   (seriesId: string) => `${V1}/series/${seriesId}/cast`,
    CREATE: (seriesId: string) => `${V1}/series/${seriesId}/cast`,
    UPDATE: (seriesId: string, creditId: string) => `${V1}/series/${seriesId}/cast/${creditId}`,
    DELETE: (seriesId: string, creditId: string) => `${V1}/series/${seriesId}/cast/${creditId}`,
    DELETE_TRANSLATION: (seriesId: string, creditId: string, lang: string) => `${V1}/series/${seriesId}/cast/${creditId}/translations/${lang}`,
  },
  EPISODE_CAST: {
    LIST:   (episodeId: string) => `${V1}/episodes/${episodeId}/cast`,
    CREATE: (episodeId: string) => `${V1}/episodes/${episodeId}/cast`,
    UPDATE: (episodeId: string, creditId: string) => `${V1}/episodes/${episodeId}/cast/${creditId}`,
    DELETE: (episodeId: string, creditId: string) => `${V1}/episodes/${episodeId}/cast/${creditId}`,
    DELETE_TRANSLATION: (episodeId: string, creditId: string, lang: string) => `${V1}/episodes/${episodeId}/cast/${creditId}/translations/${lang}`,
  },
} as const;

// ── Campaign endpoints (campaign config + embedded landing page fields) ──
export const CAMPAIGN_ENDPOINTS = {
  LIST:     `${V1}/growth/campaigns`,
  CREATE:   `${V1}/growth/campaigns`,
  DETAIL:   (id: string) => `${V1}/growth/campaigns/${id}`,
  UPDATE:   (id: string) => `${V1}/growth/campaigns/${id}`,
  VALIDATE: (id: string) => `${V1}/growth/campaigns/${id}/validate`,
  ACTIVATE: (id: string) => `${V1}/growth/campaigns/${id}/activate`,
  PAUSE:    (id: string) => `${V1}/growth/campaigns/${id}/pause`,
  ARCHIVE:  (id: string) => `${V1}/growth/campaigns/${id}/archive`,
  SUMMARY:  (id: string) => `${V1}/growth/campaigns/${id}/summary`,
} as const;

// ── Feed endpoints (shelves + hero banners) ──
export const FEED_ENDPOINTS = {
  // Shelves
  SHELVES:             `${V1}/feed/shelves`,
  SHELF:               (id: string) => `${V1}/feed/shelves/${id}`,
  SHELF_REORDER:       `${V1}/feed/shelves/reorder`,
  // Shelf items
  SHELF_ITEMS:         (shelfId: string) => `${V1}/feed/shelves/${shelfId}/items`,
  SHELF_ITEM:          (shelfId: string, itemId: string) => `${V1}/feed/shelves/${shelfId}/items/${itemId}`,
  SHELF_ITEMS_REORDER: (shelfId: string) => `${V1}/feed/shelves/${shelfId}/items/reorder`,
  SHELF_DELETE_TRANSLATION: (shelfId: string, lang: string) => `${V1}/feed/shelves/${shelfId}/translations/${lang}`,
  // Hero banners
  BANNERS:             `${V1}/feed/hero`,
  BANNER:              (id: string) => `${V1}/feed/hero/${id}`,
  BANNERS_REORDER:     `${V1}/feed/hero/reorder`,
  BANNER_DELETE_TRANSLATION: (bannerId: string, lang: string) => `${V1}/feed/hero/${bannerId}/translations/${lang}`,
} as const;

// ── Story submission endpoints (read-only in Phase 1) ──
export const STORY_ENDPOINTS = {
  LIST:   `${V1}/stories`,
  DETAIL: (id: string) => `${V1}/stories/${id}`,
} as const;

// ── Subtitle endpoints (scoped under episodes) ──
export const SUBTITLE_ENDPOINTS = {
  LIST:            (episodeId: string) => `${V1}/episodes/${episodeId}/subtitles`,
  CREATE:          (episodeId: string) => `${V1}/episodes/${episodeId}/subtitles`,
  COVERAGE:        (episodeId: string) => `${V1}/episodes/${episodeId}/subtitles/coverage`,
  SERIES_COVERAGE: (seriesId: string)  => `${V1}/series/${seriesId}/subtitles/coverage`,
  PREVIEW_URL:     (episodeId: string, subtitleId: string) => `${V1}/episodes/${episodeId}/subtitles/${subtitleId}/preview-url`,
  UPDATE:          (episodeId: string, subtitleId: string) => `${V1}/episodes/${episodeId}/subtitles/${subtitleId}`,
  DELETE:          (episodeId: string, subtitleId: string) => `${V1}/episodes/${episodeId}/subtitles/${subtitleId}`,
} as const;

// ── Commerce: Key Packs (catalog management — create/edit/publish to Apple+Google) ──
export const KEYS_ENDPOINTS = {
  LIST:    `${V1}/commerce/keys/packs`,
  CREATE:  `${V1}/commerce/keys/packs`,
  DETAIL:  (id: string) => `${V1}/commerce/keys/packs/${id}`,
  UPDATE:  (id: string) => `${V1}/commerce/keys/packs/${id}`,
  PUBLISH: (id: string) => `${V1}/commerce/keys/packs/${id}/publish`,
  APPLE_PRICE_POINTS: (id: string) => `${V1}/commerce/keys/packs/${id}/apple-price-points`,
  APPLE_PRICE:        (id: string) => `${V1}/commerce/keys/packs/${id}/apple-price`,
  GOOGLE_PRICE:       (id: string) => `${V1}/commerce/keys/packs/${id}/google-price`,
  GOOGLE_LISTING:     (id: string) => `${V1}/commerce/keys/packs/${id}/google-listing`,
  APPLE_LOCALIZATION: (id: string) => `${V1}/commerce/keys/packs/${id}/apple-localization`,
  APPLE_REVIEW_NOTE:       (id: string) => `${V1}/commerce/keys/packs/${id}/apple-review-note`,
  APPLE_REVIEW_SCREENSHOT: (id: string) => `${V1}/commerce/keys/packs/${id}/apple-review-screenshot`,
  APPLE_REVIEW_PREVIEW:    (id: string) => `${V1}/commerce/keys/packs/${id}/apple-review-preview`,
  APPLE_REVIEW_SCREENSHOT_URL: (id: string) => `${V1}/commerce/keys/packs/${id}/apple-review-screenshot-url`,
  APPLE_REVIEW_REMOVE_ITEM: (id: string, itemId: string) => `${V1}/commerce/keys/packs/${id}/apple-review-items/${itemId}`,
  APPLE_REVIEW_REMOVE_OWN_ITEM: (id: string) => `${V1}/commerce/keys/packs/${id}/apple-review-own-item`,
  APPLE_REVIEW_SUBMIT:     (id: string) => `${V1}/commerce/keys/packs/${id}/apple-review-submit`,
  APPLE_REVIEW_REFRESH:    (id: string) => `${V1}/commerce/keys/packs/${id}/apple-review-refresh`,
  LEDGER:                    `${V1}/commerce/keys/ledger`,
  LEDGER_EXPORT:             `${V1}/commerce/keys/ledger/export`,
  RECONCILIATION_EXCEPTIONS: `${V1}/commerce/keys/reconciliation-exceptions`,
} as const;

// ── Commerce: Subscription Plans (catalog management — create/edit/publish to Apple+Google) ──
export const SUBSCRIPTION_PLAN_ENDPOINTS = {
  LIST:    `${V1}/commerce/subscriptions/plans`,
  CREATE:  `${V1}/commerce/subscriptions/plans`,
  DETAIL:  (id: string) => `${V1}/commerce/subscriptions/plans/${id}`,
  UPDATE:  (id: string) => `${V1}/commerce/subscriptions/plans/${id}`,
  PUBLISH: (id: string) => `${V1}/commerce/subscriptions/plans/${id}/publish`,
  APPLE_PRICE_POINTS: (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-price-points`,
  APPLE_PRICE:        (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-price`,
  GOOGLE_PRICE:       (id: string) => `${V1}/commerce/subscriptions/plans/${id}/google-price`,
  GOOGLE_LISTING:     (id: string) => `${V1}/commerce/subscriptions/plans/${id}/google-listing`,
  APPLE_LOCALIZATION: (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-localization`,
  APPLE_REVIEW_NOTE:       (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-review-note`,
  APPLE_REVIEW_SCREENSHOT: (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-review-screenshot`,
  APPLE_REVIEW_PREVIEW:    (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-review-preview`,
  APPLE_REVIEW_SCREENSHOT_URL: (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-review-screenshot-url`,
  APPLE_REVIEW_REMOVE_ITEM: (id: string, itemId: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-review-items/${itemId}`,
  APPLE_REVIEW_REMOVE_OWN_ITEM: (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-review-own-item`,
  APPLE_REVIEW_SUBMIT:     (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-review-submit`,
  APPLE_REVIEW_REFRESH:    (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-review-refresh`,
  // ── Apple Subscription Group — addressed via a representative plan_id,
  // resolved server-side to the one group shared across this plan's family ──
  APPLE_GROUP:                (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-group`,
  APPLE_GROUP_LOCALIZATION:   (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-group-localization`,
  APPLE_GROUP_REVIEW_PREVIEW: (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-group-review-preview`,
  APPLE_GROUP_REVIEW_REMOVE_ITEM: (id: string, itemId: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-group-review-items/${itemId}`,
  APPLE_GROUP_REVIEW_SUBMIT:  (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-group-review-submit`,
  APPLE_GROUP_REVIEW_REFRESH: (id: string) => `${V1}/commerce/subscriptions/plans/${id}/apple-group-review-refresh`,
} as const;

// ── Commerce: Subscription (per-user entitlement viewer, ADM-SUBS-02) ────────
export const SUBSCRIPTION_ENDPOINTS = {
  LIST:                             `${V1}/commerce/subscriptions`,
  DETAIL:                    (userId: string) => `${V1}/commerce/subscriptions/${userId}`,
  RECEIPTS:                  (userId: string) => `${V1}/commerce/subscriptions/${userId}/receipts`,
  RECONCILIATION_EXCEPTIONS: (userId: string) => `${V1}/commerce/subscriptions/${userId}/reconciliation-exceptions`,
  RECONCILIATION_EXCEPTIONS_GLOBAL: `${V1}/commerce/subscriptions/reconciliation-exceptions`,
  GRANT:                     (userId: string) => `${V1}/commerce/subscriptions/${userId}/grant`,
} as const;

// ── Commerce: Wallet (ADM-KEYS-03 — support wallet/unlock-history lookup) ────
export const WALLET_ENDPOINTS = {
  DETAIL:                    (userId: string) => `${V1}/commerce/wallet/${userId}`,
  TRANSACTIONS:              (userId: string) => `${V1}/commerce/wallet/${userId}/transactions`,
  UNLOCK_HISTORY:            (userId: string) => `${V1}/commerce/wallet/${userId}/unlock-history`,
  ADJUST:                    (userId: string) => `${V1}/commerce/wallet/${userId}/adjust`,
  RECONCILIATION_EXCEPTIONS: (userId: string) => `${V1}/commerce/wallet/${userId}/reconciliation-exceptions`,
} as const;

// ── Paywall settings (US-06.14 — Daily Ad Unlock Limit) ──────────────────────
export const PAYWALL_ENDPOINTS = {
  AD_UNLOCK_LIMIT: `${V1}/commerce/paywall/ad-unlock-limit`,
  EPISODE_KEYS_DEFAULT: `${V1}/commerce/keys/episode-default`,
} as const;

// ── Support: Notes & Correction-Request escalations (ADM-KEYS-03) ────────────
export const SUPPORT_ENDPOINTS = {
  NOTES_CREATE:        `${V1}/support/notes`,
  NOTES_BY_USER:       (userId: string) => `${V1}/support/users/${userId}/notes`,
  CORRECTION_REQUESTS: `${V1}/support/correction-requests`,
  CORRECTION_APPROVE:  (id: string) => `${V1}/support/correction-requests/${id}/approve`,
  CORRECTION_REJECT:   (id: string) => `${V1}/support/correction-requests/${id}/reject`,
} as const;

// ── Payment: Transactions, Webhook Events, Refunds (ADM-PAY-01) ──────────────
export const PAYMENT_ENDPOINTS = {
  TRANSACTIONS:                `${V1}/payment/transactions`,
  TRANSACTION_DETAIL:         (id: string) => `${V1}/payment/transactions/${id}`,
  TRANSACTION_ENTITLEMENT:    (id: string) => `${V1}/payment/transactions/${id}/entitlement`,
  TRANSACTION_WEBHOOK_EVENTS: (id: string) => `${V1}/payment/transactions/${id}/webhook-events`,
  TRANSACTION_REFUNDS:        (id: string) => `${V1}/payment/transactions/${id}/refunds`,

  // ── Reconciliation (ADM-PAY-02) ──
  RECONCILIATION_ITEMS:       `${V1}/payment/reconciliation-items`,
  RECONCILIATION_ITEM_DETAIL: (id: string) => `${V1}/payment/reconciliation-items/${id}`,
  RECONCILIATION_SUMMARY:     `${V1}/payment/reconciliation-items/summary`,
  RECONCILIATION_EXPORT:      `${V1}/payment/reconciliation-items/export`,
  RECONCILIATION_RESOLVE:     (id: string) => `${V1}/payment/reconciliation-items/${id}/resolve`,
} as const;
